import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, Button, Input, Badge } from '@b2b/ui-components';
import { Send, Paperclip, X, Download, Image as ImageIcon, FileText } from 'lucide-react';
import { rfqService } from '@b2b/services';
import { RfqMessage } from '@b2b/services/api/modules/rfq/rfq.types';
import { formatDistanceToNow } from 'date-fns';

interface RFQMessageThreadProps {
  rfqId: string;
  currentUserId: string;
  onMessageSent?: () => void;
}

export const RFQMessageThread: React.FC<RFQMessageThreadProps> = ({
  rfqId,
  currentUserId,
  onMessageSent,
}) => {
  const [messages, setMessages] = useState<RfqMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<string[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchMessages();
    // Auto-scroll to bottom
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const response = await rfqService.getMessages(rfqId, { page: 1, limit: 100 });
      setMessages(response.items || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter((file) => {
      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert(`File ${file.name} exceeds 10MB limit`);
        return false;
      }
      // Validate file type
      const allowedTypes = [
        'application/pdf',
        'image/png',
        'image/jpeg',
        'image/jpg',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      ];
      if (!allowedTypes.includes(file.type)) {
        alert(`File ${file.name} type not allowed. Allowed: PDF, PNG, JPG, XLSX`);
        return false;
      }
      return true;
    });
    setSelectedFiles((prev) => [...prev, ...validFiles]);
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadFiles = async (): Promise<string[]> => {
    if (selectedFiles.length === 0) return [];

    const mediaIds: string[] = [];
    setUploadingFiles(selectedFiles.map((f) => f.name));

    try {
      // Upload files via MediaService (assuming there's a mediaService in @b2b/services)
      // For now, we'll need to upload via admin/media/upload endpoint
      // This is a placeholder - actual implementation depends on your media service structure
      for (const file of selectedFiles) {
        const formData = new FormData();
        formData.append('file', file);
        // TODO: Upload via media service and get media ID
        // const response = await mediaService.uploadFile(formData);
        // mediaIds.push(response.id);
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      throw error;
    } finally {
      setUploadingFiles([]);
    }

    return mediaIds;
  };

  const handleSendMessage = async () => {
    if (!messageText.trim() && selectedFiles.length === 0) return;

    try {
      setSending(true);

      // Upload files first
      const attachmentIds = await uploadFiles();

      // Send message
      await rfqService.postMessage(rfqId, {
        message: messageText,
        attachments: attachmentIds,
      });

      setMessageText('');
      setSelectedFiles([]);
      await fetchMessages();
      onMessageSent?.();
    } catch (error: any) {
      console.error('Error sending message:', error);
      alert(error?.response?.data?.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) return ImageIcon;
    return FileText;
  };

  const formatDate = (dateString: string) => {
    return formatDistanceToNow(new Date(dateString), { addSuffix: true });
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-muted-foreground">
          Loading messages...
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Messages</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {/* Messages List */}
        <div className="h-96 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 ? (
            <div className="text-center text-muted-foreground py-8">
              No messages yet. Start the conversation!
            </div>
          ) : (
            messages.map((message) => {
              const isCurrentUser = message.senderId === currentUserId;
              return (
                <div
                  key={message.id}
                  className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[70%] rounded-lg p-3 ${
                      isCurrentUser
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-foreground'
                    }`}
                  >
                    {!isCurrentUser && message.sender && (
                      <div className="text-xs font-medium mb-1 opacity-80">
                        {message.sender.firstName} {message.sender.lastName}
                      </div>
                    )}
                    <p className="text-sm whitespace-pre-wrap">{message.message}</p>
                    {message.attachments && message.attachments.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {message.attachments.map((attachmentId, idx) => (
                          <div
                            key={idx}
                            className="flex items-center gap-2 text-xs opacity-90"
                          >
                            <FileText className="w-3 h-3" />
                            <span>Attachment {idx + 1}</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-4 px-1 text-xs"
                              onClick={() => {
                                // TODO: Download attachment via media service
                                window.open(`/api/media/${attachmentId}`, '_blank');
                              }}
                            >
                              <Download className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="text-xs opacity-70 mt-1">{formatDate(message.createdAt)}</div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Selected Files Preview */}
        {selectedFiles.length > 0 && (
          <div className="border-t p-2 space-y-1">
            {selectedFiles.map((file, index) => (
              <div
                key={index}
                className="flex items-center justify-between text-xs bg-muted p-2 rounded"
              >
                <span className="truncate flex-1">{file.name}</span>
                <span className="text-muted-foreground mx-2">
                  {(file.size / (1024 * 1024)).toFixed(2)}MB
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => removeFile(index)}
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Message Input */}
        <div className="border-t p-4">
          <div className="flex gap-2">
            <label className="cursor-pointer">
              <input
                type="file"
                multiple
                accept=".pdf,.png,.jpg,.jpeg,.xlsx"
                onChange={handleFileSelect}
                className="hidden"
              />
              <Button variant="ghost" size="sm" type="button" asChild>
                <span>
                  <Paperclip className="w-4 h-4" />
                </span>
              </Button>
            </label>
            <Input
              placeholder="Type a message..."
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              className="flex-1"
            />
            <Button
              onClick={handleSendMessage}
              disabled={sending || (!messageText.trim() && selectedFiles.length === 0)}
              size="sm"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Max file size: 10MB. Allowed: PDF, PNG, JPG, XLSX
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

